import { effect, inject, Injectable, linkedSignal, signal, untracked } from "@angular/core";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { Project } from "../../../models/project";
import { JobStateService } from "./job-state.store";
import { ModelViewerSettingsService } from "./model-viewer-settings.service";

const DEFAULT_MIN_MAX = {min: null, max: null}

export interface MinMax{
  min: number,
  max: number
}

interface SettingEntry {
  data: Record<string, any>;
  lastModified: number; // Timestamp for pruning
}

interface SettingsStore {
  [id: string]: SettingEntry;
}

export type SliceType = 'inline' | 'crossline' | 'depth'

type TrueMinMaxType = Record<SliceType, number>

@Injectable({ providedIn: "root" })
export class ViewerSettingsService {
  private readonly GLOBAL_STORAGE_KEY = "globalViewerSettings";

  private jobStateStore  = inject(JobStateService)
  private modelViewerSettings = inject(ModelViewerSettingsService)
  private cachedProjectService = inject(CachedProjectService)
  private currentProject = toSignal(this.cachedProjectService.currentProject)

  // global settings
  viewerHeight = signal<number>(400);
  depthViewerHeight = signal<number>(300)
  gridLines = signal<boolean>(true);
  smoothing = signal<boolean>(true)
  fullWidthIlXl = signal<boolean>(false)
  showDepthSlice = signal<boolean>(false)
  verticalProfileMode = signal<'show' | 'hide' | 'besideInline'>('show')
  showSonicLocations = signal<boolean>(false)
  showShotOverlay = signal<boolean>(false)
  showOnlyUsedShots = signal<boolean>(false)
  depthShotRadius = signal<number>(3)
  selectedShotOverlayMetric = signal<string>('traces_fit')
  shotColorMin = signal<number>(0)
  shotColorMax = signal<number>(100)
  showWellMarkers = signal<boolean>(true)
  showSonicLog = signal<boolean>(true)
  showSonicSamples = signal<boolean>(false)
  axisUnits = signal<'km' | 'kft'>('km')

  screenshotSettings = {
    withVP: signal<boolean>(false), 
    withDepth: signal<boolean>(false), 
  }

  sliceStarts = {IL: signal<number>(null), XL: signal<number>(null), depth: signal<number>(null)}
  sliceEnds = {IL: signal<number>(null), XL: signal<number>(null), depth: signal<number>(null)}

  // viewer true values

  trueMin = signal<TrueMinMaxType>({
      inline: null, 
      crossline: null,
      depth: null
  })
  trueMax = signal<TrueMinMaxType>({
      inline: null, 
      crossline: null,
      depth: null
  })

  auto = linkedSignal<{jobId: string, selectedModel: string}, boolean>({
    source: () => ({
        jobId: this.jobStateStore.jobData().id,
        selectedModel : this.jobStateStore.selectedModel()
      }),
    computation: (source, previous) => {
      let syncSettings = untracked(() => this.jobStateStore.synchroniseSettings())
      // strict undefined check, cause previous?.value could just have a false as its value which would be a valid value in reality
      if ( syncSettings && previous?.value !== undefined){ // if sync settings, forget all else and just persist
        return previous.value
      }
      // time to get the cache values
      try{
        let cachedValue = this.modelViewerSettings.savedModelSettings[source.jobId][source.selectedModel].auto 
        return cachedValue
      }catch(e){
        // nothing to do here, continue. it was just easier to do a nested existence check like this
      }
      return false
    },
  })
  inlineMinMax = linkedSignal<{project: Project, jobId: string, selectedModel: string}, MinMax>({
    source: () => {
      // job and project and what are influencers
      // if the job changes, so does the project (and doesn't if same project). so the project kind of doesn't HAVE to be in the source
      // but we'll still add for the sake of perhaps clarity that project does sort of influence if given that the value may be coming from project defaults
      return {
        project: this.currentProject(),
        jobId: this.jobStateStore.jobData().id,
        selectedModel : this.jobStateStore.selectedModel()
      }
    },
    computation: (source, previous) => {
      // sync settings is not in source cause that doesn't cause the min max to be 'recalculated' really
      let syncSettings = untracked(() => this.jobStateStore.synchroniseSettings())
      if ( syncSettings && previous?.value && source.selectedModel === previous.source.selectedModel){ // if sync settings, forget all else and just persist BUT it is also necessary for it to be the same model if we are about to persist the min and max
        return previous.value
      }

      // time to get the cache values
      try{
        let cachedValue = this.modelViewerSettings.savedModelSettings[source.jobId][source.selectedModel].inlineMixMax 
        if (cachedValue.min && cachedValue.max){
          return cachedValue
        }
      }catch(e){
        // nothing to do here, continue. it was just easier to do a nested existence check like this
      }

      // if we reach here, sync is off, and last resort before just using the defaults
      if (source.project){
        let model = source.selectedModel
        return source.project.displayVariables[model] ?? DEFAULT_MIN_MAX
      }
      return DEFAULT_MIN_MAX
    },
    equal: (current, prev) => current.max == prev.max && current.min == prev.min
  })
  // for now, crossline min max is gonna be the same as the inlineMinMax
  // crosslineMinMax = signal<MinMax>({min: null, max: null})
  depthMinMax = linkedSignal<{project: Project, jobId: string, selectedModel: string}, MinMax>({
    source: () => {
      // job and project and what are influencers
      // if the job changes, so does the project (and doesn't if same project). so the project kind of doesn't HAVE to be in the source
      // but we'll still add for the sake of perhaps clarity that project does sort of influence if given that the value may be coming from project defaults
      return {
        project: this.currentProject(),
        jobId: this.jobStateStore.jobData().id,
        selectedModel : this.jobStateStore.selectedModel()
      }
    },
    computation: (source, previous) => {
      // sync settings is not in source cause that doesn't cause the min max to be 'recalculated' really
      let syncSettings = untracked(() => this.jobStateStore.synchroniseSettings())
      if ( syncSettings && previous?.value && source.selectedModel === previous.source.selectedModel){ // if sync settings, forget all else and just persist
        return previous.value
      }

      // time to get the cache values
      try{
        let cachedValue = this.modelViewerSettings.savedModelSettings[source.jobId][source.selectedModel].depthMinMax 
        if (cachedValue.min && cachedValue.max){
          return cachedValue
        }
      }catch(e){
        // nothing to do here, continue. it was just easier to do a nested existence check like this
      }

      // if we reach here, sync is off, and last resort before just using the defaults
      if (source.project){
        let model = source.selectedModel
        return source.project.displayVariables[model] ?? DEFAULT_MIN_MAX
      }
      return DEFAULT_MIN_MAX
    },
    equal: (current, prev) => current.max == prev.max && current.min == prev.min
  })

  constructor() {
    this.loadGlobalSettings();
    effect(() => this.saveGlobalSettings());

    effect(() => {
      // if the screenshotSettings.withDepth is set to true, you the showDepthSlice should also be set to tru if not already true
      if (this.screenshotSettings.withDepth()){
        untracked(() => {
          if (!this.showDepthSlice()) this.showDepthSlice.set(true) // set to true if is currently false
        })
      }
    })
  }

  saveGlobalSettings() {
    const data = {
      viewerHeight: this.viewerHeight(),
      gridLines: this.gridLines(),
      smoothing: this.smoothing(),
      fullWidthIlXl: this.fullWidthIlXl(),
      verticalProfileMode: this.verticalProfileMode(),
      showSonicLocations: this.showSonicLocations(),
      depthViewerHeight: this.depthViewerHeight(),
      showShotOverlay: this.showShotOverlay(),
      showOnlyUsedShots: this.showOnlyUsedShots(),
      depthShotRadius: this.depthShotRadius(),
      showWellMarkers: this.showWellMarkers(),
      showSonicLog: this.showSonicLog(),
      showSonicSamples: this.showSonicSamples(),

      screenshotSettings: {
        withVP: this.screenshotSettings.withVP(),
        withDepth: this.screenshotSettings.withDepth()
      }
    };
    localStorage.setItem(this.GLOBAL_STORAGE_KEY, JSON.stringify(data));
  }

  loadGlobalSettings() {
    const rawData = localStorage.getItem(this.GLOBAL_STORAGE_KEY);
    const data = rawData ? JSON.parse(rawData) : {};

    this.viewerHeight.set(data["viewerHeight"] ?? 400);
    this.depthViewerHeight.set(data["depthViewerHeight"] ?? 300)
    this.gridLines.set(data["gridLines"] ?? true);
    this.smoothing.set(data["smoothing"] ?? true);
    this.fullWidthIlXl.set(data["fullWidthIlXl"] ?? false)
    this.verticalProfileMode.set(data["verticalProfileMode"] ?? 'show')
    this.showWellMarkers.set(data["showWellMarkers"] ?? true)
    this.showSonicLog.set(data["showSonicLog"] ?? true)
    this.showSonicSamples.set(data["showSonicSamples"] ?? false)
    
    // specific to depth slice
    this.showSonicLocations.set(data["showSonicLocations"] ?? false)
    this.showShotOverlay.set(data["showShotOverlay"] ?? false)
    this.showOnlyUsedShots.set(data["showOnlyUsedShots"] ?? false)
    this.depthShotRadius.set(data["depthShotRadius"] ?? 3)

    this.screenshotSettings.withVP.set((data['screenshotSettings'] && data['screenshotSettings']['withVP']) ?? false)
    this.screenshotSettings.withDepth.set((data['screenshotSettings'] && data['screenshotSettings']['withDepth']) ?? false)
  }

  setTrueLimits(sliceName: SliceType, trueMax: number, trueMin: number){
    // the update ensures its a object key mutation using the previous value
    this.trueMax.update(current => ({
      ...current,
      [sliceName]: trueMax
    }));
    this.trueMin.update(current => ({
      ...current,
      [sliceName]: trueMin
    }));
  }
}
